import 'regenerator-runtime/runtime';
import { call, cancel, select, takeEvery } from 'redux-saga/effects';

import {
  getAuslandsRisikoZuschlag,
  getGeschaeftsart,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  VorschlagsdatenRequestPayload,
} from '@ps20-frontend/shop-integration-redux';
import {
  AdresseAntrag,
  EinheitenArt,
  Feature,
  RsDatenAntrag,
  SparteAntrag,
  SpartenType,
  VerkehrBaustein,
  VermieteteEinheit,
  VermietetesGrundstueck,
  VermietetesObjektAntrag,
  VorschadenAntrag,
  VorschadenArt,
} from '@ps20/bff-models';
import {
  App,
  convertToBffLaufzeit,
  DIPLOMATEN_CODE,
  getFachschluessel,
  mapToBffVorvertragsNummer,
  Produktlinie,
  VermietetesBebautesObjekt,
  VermietetesObjekt,
  VermietetesObjektEnum,
  VermietetesUnbebautesObjekt,
  VersicherterBereichEnum,
  Vertragsoptionen,
} from '@ps20-frontend/frontend-models';
import dayjs from 'dayjs';
import { convertBasisAdresseToBffAdresse } from '@ps20-frontend/vorschlagsdaten-lib';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { DATEFORMAT_SHORT_ISO } from '@ps20-frontend/utils';
import { mapGesamtbeitragIfJaehrlichAndBeitragIsWrong } from '@ps20-frontend/preis-lib';
import { selectFeatureToggle } from '@ps20-frontend/selector';
import { IAppState } from '../appState';
import { selectSelectedProduktlinie } from '../../app/shared/selectors/produkt.selector';
import { VertragsoptionenRS } from '../models/vertragsoptionen.model';

export function* watchVorschlagdatenChanged() {
  yield takeEvery(GlobalActions.VORSCHLAGDATEN_REQUESTED, safeSaga(vorschlagdatenSaga, App.RS));
}

export function* vorschlagdatenSaga(action: GlobalAction<VorschlagsdatenRequestPayload>) {
  const { angebotsIds } = action.payload;

  if (!angebotsIds.some(angebot => angebot.appId === App.RS)) {
    yield cancel();
    return;
  }

  const state: IAppState = yield select();
  const globalPut = GlobalPut.getInstance();

  const ausgewaehlteProduktlinie = selectSelectedProduktlinie(state);
  const vorteilsRabattActivated = selectFeatureToggle(state, Feature.VORTEILSRABATT);

  const vorschlagdaten: RsDatenAntrag = {
    spartenType: SpartenType.RECHTSSCHUTZ,
    jahresbeitrag: state.jahresbeitrag,
    angebotsdatum: vorteilsRabattActivated ? state.vertragsoptionen.angebotsdatum : undefined,
    savedJahresbeitragMitRabatt: state.savedJahresbeitragMitRabatt,
    gesamtpreisNachZahlweise: mapGesamtbeitragIfJaehrlichAndBeitragIsWrong(
      state.vertragsoptionen.zahlungsweise,
      state.gesamtbeitragMitRabatt,
      state.jahresbeitrag,
      App.RS,
    ),
    savedGesamtbeitragMitRabatt: state.savedGesamtbeitragMitRabatt,
    familienStand: state.versicherungsnehmer.familienstand,
    vnAdresse: convertBasisAdresseToBffAdresse(state.risikoort),
    imOeffentlichenDienstTaetig: state.imOeffentlichenDienstTaetig
      ? state.imOeffentlichenDienstTaetig
      : false,
    selbstaendigTaetig: state.selbststaendigkeit.selbststaendig
      ? state.selbststaendigkeit.selbststaendig
      : false,
    vermieteteObjekte: convertVermieteteObjekte(state),
    vermieteteGrundstuecke: convertVermieteteGrundstuecke(state),
    verkehrBaustein: convertKfz(state),
    antragsId: state.antragsnummer,
    sicherungsglaeubiger: null,
    hspLfnr: state.produkt.hspLfnr,
    auslandsRisikoZuschlag: getAuslandsRisikoZuschlag(
      state.vertragsoptionen?.selectedKooperation === DIPLOMATEN_CODE,
      ausgewaehlteProduktlinie.nachlass,
    ),
    produkt: {
      beginn: null,
      ablauf: null,
      selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
      produktline: ausgewaehlteProduktlinie.produktName,
      kurztext: ausgewaehlteProduktlinie.kurztext,
      langtext: ausgewaehlteProduktlinie.langtext,
      fachschluessel: getFachschluessel(state.produkt),
    },
    vorschaeden: convertToVorschaeden(state),
    rbdMax: ausgewaehlteProduktlinie.rbdMax,
    rbdAktuell: state.taasInfo.rbdNachlass,
    treuebonus: state.taasInfo.tbNachlass,
    savedRbdNachlass: state.taasInfo.savedRbdNachlass,
    savedTbNachlass: state.taasInfo.savedTbNachlass,
    vorteilsrabatt: state.taasInfo.vorteilsrabatt,
    preisverzeichnis: ausgewaehlteProduktlinie.preisverzeichnis,
    vorversicherung: [],
    antragSpeichernDatum: dayjs().format(DATEFORMAT_SHORT_ISO),
    laufzeit: convertToBffLaufzeit(state.vertragsoptionen.vertragslaufzeit),
    rabattstufe: state.rabattstufe,
    geschaeftsart: getGeschaeftsart(state.vertragsoptionen),
    vorvertragsnummern: mapToBffVorvertragsNummer(state.vertragsoptionen.vorvertragsNummern),
    vorvertragGekuendigt: state.vertragsoptionen.vorvertragGekuendigt,
    zielmarktAbweichung: false,
    wartezeitverzicht: null,
    kooperation: state.vertragsoptionen.selectedKooperation,
  };

  handleLinieFromRsVorvertrag(vorschlagdaten, ausgewaehlteProduktlinie, state.vertragsoptionen);

  yield call(globalPut.put, new GlobalActions().vorschlagsdatenGenerated(vorschlagdaten));
}

export const handleLinieFromRsVorvertrag = function (
  sparteAntrag: SparteAntrag,
  selectedProduktlinie: Produktlinie,
  vertragsoptionen: Vertragsoptionen,
): SparteAntrag {
  if (vertragsoptionen.vorvertragLinie) {
    sparteAntrag.ersatzgeschaeftLinieGewechselt = selectedLinieOtherThanRsVorvertrag(
      selectedProduktlinie,
      vertragsoptionen,
    );
  }
  return sparteAntrag;
};

function selectedLinieOtherThanRsVorvertrag(
  selectedProduktlinie: Produktlinie,
  vertragsoptionen: Vertragsoptionen,
): boolean {
  const vertragsOptionenRs = vertragsoptionen as VertragsoptionenRS;
  const vorvertragLinie = vertragsoptionen.vorvertragLinie;
  return (
    vorvertragLinie != null &&
    vorvertragLinie !== '' &&
    (!vorvertragLinie.includes(selectedProduktlinie.produktName) ||
      absicherungHasChanged(vertragsOptionenRs, selectedProduktlinie.langtext))
  );
}

function absicherungHasChanged(vertragsoptionen: VertragsoptionenRS, langtext: string) {
  if (langtext.includes('Verkehr')) {
    return !vertragsoptionen.fahrzeug;
  }
  return !!vertragsoptionen.fahrzeug;
}

const DEFAULT_BETRACHTUNGSZEITRAUM_RS = 5;

export function convertVermieteteGrundstuecke(state: IAppState): Array<VermietetesGrundstueck> {
  const result: Array<VermietetesGrundstueck> = new Array<VermietetesGrundstueck>();
  const vermieteteObjekte: Array<VermietetesObjekt> = state.vermieteteObjekte;
  if (!vermieteteObjekte) {
    return;
  }
  vermieteteObjekte.forEach(vermietetesObjektFrontend => {
    const bebaut = vermietetesObjektFrontend.grundstueckBebaut === VermietetesObjektEnum.BEBAUT;
    if (!bebaut) {
      const adresseAntrag: AdresseAntrag = {
        strasse: vermietetesObjektFrontend.strasse,
        hausnummer: vermietetesObjektFrontend.hausnummer,
        ort: vermietetesObjektFrontend.ort,
        plz: vermietetesObjektFrontend.plz,
      };
      const vermietetesGruendstuck: VermietetesUnbebautesObjekt = vermietetesObjektFrontend;
      result.push({
        adresseAntrag: adresseAntrag,
        unbebauteFlaeche: vermietetesGruendstuck.grundstueckFlaeche,
        flurstueckNummer: vermietetesGruendstuck.flurstueckNummer,
        plzFaktor: vermietetesGruendstuck.plzFaktorUnbebaut,
        deltafaktor: vermietetesGruendstuck.deltafaktorUnbebaut,
      });
    }
  });
  return result;
}

export function convertKfz(state: IAppState): VerkehrBaustein {
  if (
    state.vertragsoptionen.versicherterBereich !== VersicherterBereichEnum.VERKEHR ||
    !state.vertragsoptionen.fahrzeug ||
    state.vertragsoptionen.fahrzeug.anzahlFahrzeug == null
  ) {
    return;
  }
  return {
    kennzeichen: null,
    fahrzeugArt: state.vertragsoptionen.fahrzeug.fahrzeugArt,
    anzahlFahrzeuge: state.vertragsoptionen.fahrzeug.anzahlFahrzeug,
  };
}

export function convertToVorschaeden(state: IAppState): VorschadenAntrag[] {
  return [
    {
      vorschadenArt: VorschadenArt.RS,
      anzahl: state.rechtsstreitigkeiten.anzahl,
      betrachtungszeitraum: DEFAULT_BETRACHTUNGSZEITRAUM_RS,
    },
  ];
}

export function convertVermieteteObjekte(state: IAppState): Array<VermietetesObjektAntrag> {
  const result: Array<VermietetesObjektAntrag> = new Array<VermietetesObjektAntrag>();
  const vermieteteObjekte: Array<VermietetesObjekt> = state.vermieteteObjekte;
  if (!vermieteteObjekte) {
    return;
  }
  vermieteteObjekte.forEach(vermietetesObjektFrontend => {
    const adresseAntrag: AdresseAntrag = {
      strasse: vermietetesObjektFrontend.strasse,
      hausnummer: vermietetesObjektFrontend.hausnummer,
      ort: vermietetesObjektFrontend.ort,
      plz: vermietetesObjektFrontend.plz,
    };
    const bebaut = vermietetesObjektFrontend.grundstueckBebaut === VermietetesObjektEnum.BEBAUT;
    if (bebaut) {
      const vermietetesGebauede: VermietetesBebautesObjekt = vermietetesObjektFrontend;
      const vermieteteEinheiten: Array<VermieteteEinheit> = new Array<VermieteteEinheit>();
      if (vermietetesGebauede.bruttojahresmieteWohneinheiten) {
        vermieteteEinheiten.push({
          jahresBruttoMiete: vermietetesGebauede.bruttojahresmieteWohneinheiten,
          einheitenArt: EinheitenArt.WOHNEINHEIT,
          anzahlVermieteteEinheiten: vermietetesGebauede.wohneinheiten,
          deltafaktor: vermietetesGebauede.deltafaktorWohnung,
          plzFaktor: vermietetesGebauede.plzFaktorWohnung,
        });
      }
      if (vermietetesGebauede.bruttojahresmieteGewerbeeinheiten) {
        vermieteteEinheiten.push({
          jahresBruttoMiete: vermietetesGebauede.bruttojahresmieteGewerbeeinheiten,
          einheitenArt: EinheitenArt.GEWERBEEINHEIT,
          anzahlVermieteteEinheiten: vermietetesGebauede.gewerbeeinheiten,
          deltafaktor: vermietetesGebauede.deltafaktorGewerbe,
          plzFaktor: vermietetesGebauede.plzFaktorGewerbe,
        });
      }
      if (vermietetesGebauede.bruttojahresmieteStellplaetze) {
        vermieteteEinheiten.push({
          jahresBruttoMiete: vermietetesGebauede.bruttojahresmieteStellplaetze,
          einheitenArt: EinheitenArt.GARAGE,
          anzahlVermieteteEinheiten: vermietetesGebauede.stellplaetze,
          deltafaktor: vermietetesGebauede.deltafaktorGarage,
          plzFaktor: vermietetesGebauede.plzFaktorGarage,
        });
      }

      result.push({
        adresseAntrag: adresseAntrag,
        vermieteteEinheiten: vermieteteEinheiten,
      } as VermietetesObjektAntrag);
    }
  });
  return result;
}
